import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Typography, Modal, Form, Input, Select, Row, Col, message } from 'antd';
import { SettingOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons';
import AMapLoader from '@amap/amap-jsapi-loader';

const { Title } = Typography;
const { Option } = Select;

interface Station {
    id: string;
    name: string;
    region: string;
    type: string;
    capacity: number;
    status: 'online' | 'offline' | 'maintenance';
    longitude?: number;  // 经度
    latitude?: number;   // 纬度
    lastUpdate: string;
}

const StationManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [editingStation, setEditingStation] = useState<Station | null>(null);
    const [form] = Form.useForm();
    const [map, setMap] = useState<any>(null);
    const [selectedCoordinates, setSelectedCoordinates] = useState<{ lng: number, lat: number } | null>(null);

    // 区域选项列表
    const regionOptions = [
        '华北地区',
        '华东地区',
        '华南地区',
        '华中地区',
        '西北地区',
        '西南地区',
        '东北地区'
    ];

    // 场站类型选项
    const stationTypeOptions = [
        '风力发电机组',
        '光伏发电单元',
        '水力发电机组',
        '储能设备',
        '变电设备'
    ];

    const [stations, setStations] = useState<Station[]>([
        {
            id: '1',
            name: '风电场A-1号机组',
            region: '华北地区',
            type: '风力发电机组',
            capacity: 3.5,
            status: 'online',
            longitude: 116.397128,
            latitude: 39.916527,
            lastUpdate: '2024-01-15 14:30'
        },
        {
            id: '2',
            name: '风电场A-2号机组',
            region: '华北地区',
            type: '风力发电机组',
            capacity: 3.5,
            status: 'online',
            longitude: 116.407128,
            latitude: 39.926527,
            lastUpdate: '2024-01-15 14:30'
        },
        {
            id: '3',
            name: '光伏场B-1号方阵',
            region: '西北地区',
            type: '光伏发电单元',
            capacity: 5.2,
            status: 'maintenance',
            longitude: 108.948024,
            latitude: 34.263161,
            lastUpdate: '2024-01-15 12:15'
        },
        {
            id: '4',
            name: '水电站C-1号机组',
            region: '西南地区',
            type: '水力发电机组',
            capacity: 50.0,
            status: 'offline',
            longitude: 104.065735,
            latitude: 30.659462,
            lastUpdate: '2024-01-15 10:00'
        },
    ]);

    const columns = [
        {
            title: '场站名称',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '所在区域',
            dataIndex: 'region',
            key: 'region',
            render: (region: string) => (
                <Tag color="blue">{region}</Tag>
            ),
        },
        {
            title: '设备类型',
            dataIndex: 'type',
            key: 'type',
        },
        {
            title: '装机容量 (MW)',
            dataIndex: 'capacity',
            key: 'capacity',
        },
        {
            title: '经纬度',
            key: 'coordinates',
            render: (_: any, record: Station) => (
                record.longitude && record.latitude ? (
                    <div>
                        <div>经度: {record.longitude.toFixed(6)}</div>
                        <div>纬度: {record.latitude.toFixed(6)}</div>
                    </div>
                ) : (
                    <span style={{ color: '#999' }}>未设置</span>
                )
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colorMap = {
                    online: 'green',
                    offline: 'red',
                    maintenance: 'orange'
                };
                const textMap = {
                    online: '在线',
                    offline: '离线',
                    maintenance: '维护中'
                };
                return <Tag color={colorMap[status as keyof typeof colorMap]}>{textMap[status as keyof typeof textMap]}</Tag>;
            },
        },
        {
            title: '最后更新',
            dataIndex: 'lastUpdate',
            key: 'lastUpdate',
        },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: Station) => (
                <div>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                        编辑
                    </Button>
                    <Button type="link" size="small">详情</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
                        删除
                    </Button>
                </div>
            ),
        },
    ];

    // 初始化高德地图
    const initMap = async (centerLng?: number, centerLat?: number) => {
        try {
            const AMap = await AMapLoader.load({
                key: 'dae1f8d2ec61a82b1e29b12ff3b0969f',
                version: '2.0',
                plugins: ['AMap.Scale', 'AMap.ToolBar'],
            });

            // 使用传入的经纬度或默认北京坐标
            const center = [
                centerLng || 116.397128,
                centerLat || 39.916527
            ];

            const mapInstance = new AMap.Map('map-container', {
                zoom: 10,
                center: center,
                mapStyle: 'amap://styles/normal',
            });

            // 添加缩放控件和工具条
            mapInstance.addControl(new AMap.Scale());
            mapInstance.addControl(new AMap.ToolBar());

            // 如果有传入的坐标，添加初始标记
            if (centerLng && centerLat) {
                const initialMarker = new AMap.Marker({
                    position: [centerLng, centerLat],
                    title: `经度: ${centerLng.toFixed(6)}, 纬度: ${centerLat.toFixed(6)}`
                });
                mapInstance.add(initialMarker);
            }

            // 点击地图获取坐标
            mapInstance.on('click', (e: any) => {
                const lng = e.lnglat.getLng();
                const lat = e.lnglat.getLat();
                setSelectedCoordinates({ lng, lat });

                // 清除之前的标记
                mapInstance.clearMap();

                // 添加标记
                const marker = new AMap.Marker({
                    position: [lng, lat],
                    title: `经度: ${lng.toFixed(6)}, 纬度: ${lat.toFixed(6)}`
                });
                mapInstance.add(marker);
            });

            setMap(mapInstance);
        } catch (error) {
            console.error('地图加载失败:', error);
            message.error('地图加载失败，请检查网络连接');
        }
    };

    const handleAdd = () => {
        setEditingStation(null);
        form.resetFields();
        setSelectedCoordinates(null);
        setIsModalOpen(true);
    };

    const handleEdit = (station: Station) => {
        setEditingStation(station);
        form.setFieldsValue(station);
        if (station.longitude && station.latitude) {
            setSelectedCoordinates({ lng: station.longitude, lat: station.latitude });
        }
        setIsModalOpen(true);
    };

    const handleDelete = (id: string) => {
        setStations(stations.filter(station => station.id !== id));
    };

    const handleMapSelect = () => {
        setIsMapModalOpen(true);
        // 延迟初始化地图，确保DOM已渲染
        setTimeout(() => {
            // 如果是编辑模式且有经纬度，使用当前经纬度作为中心点
            if (editingStation && editingStation.longitude && editingStation.latitude) {
                initMap(editingStation.longitude, editingStation.latitude);
            } else {
                // 否则使用默认中心点（北京）
                initMap();
            }
        }, 100);
    };

    const handleMapConfirm = () => {
        if (selectedCoordinates) {
            form.setFieldsValue({
                longitude: selectedCoordinates.lng,
                latitude: selectedCoordinates.lat
            });
            setIsMapModalOpen(false);
            message.success('坐标已选择');
        } else {
            message.warning('请先在地图上点击选择位置');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // 确保经纬度是数字类型
            if (values.longitude) {
                values.longitude = parseFloat(values.longitude);
            }
            if (values.latitude) {
                values.latitude = parseFloat(values.latitude);
            }

            if (editingStation) {
                // 编辑
                setStations(stations.map(station =>
                    station.id === editingStation.id ? { ...station, ...values } : station
                ));
            } else {
                // 新增
                const newStation: Station = {
                    id: Date.now().toString(),
                    ...values,
                    lastUpdate: new Date().toLocaleString('zh-CN')
                };
                setStations([...stations, newStation]);
            }

            setIsModalOpen(false);
            form.resetFields();
            setSelectedCoordinates(null);
        } catch (error) {
            console.log('验证失败:', error);
        }
    };

    return (
        <div className="page-transition">
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Title level={2} style={{ margin: 0 }}>
                        <SettingOutlined style={{ marginRight: '8px' }} />
                        场站管理
                    </Title>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        新增场站
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={stations}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1200 }}
                />
            </Card>

            {/* 新增/编辑模态框 */}
            <Modal
                title={editingStation ? '编辑场站' : '新增场站'}
                open={isModalOpen}
                onOk={handleSubmit}
                onCancel={() => setIsModalOpen(false)}
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        status: 'online'
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="场站名称"
                                rules={[{ required: true, message: '请输入场站名称' }]}
                            >
                                <Input placeholder="请输入场站名称" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="region"
                                label="所在区域"
                                rules={[{ required: true, message: '请选择所在区域' }]}
                            >
                                <Select placeholder="请选择所在区域">
                                    {regionOptions.map(region => (
                                        <Option key={region} value={region}>{region}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="type"
                                label="设备类型"
                                rules={[{ required: true, message: '请选择设备类型' }]}
                            >
                                <Select placeholder="请选择设备类型">
                                    {stationTypeOptions.map(type => (
                                        <Option key={type} value={type}>{type}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="capacity"
                                label="装机容量 (MW)"
                                rules={[{ required: true, message: '请输入装机容量' }]}
                            >
                                <Input type="number" placeholder="请输入装机容量" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="longitude"
                                label="经度"
                                rules={[
                                    { pattern: /^-?((1[0-7]\d)|(0?\d{1,2}))(\.\d+)?$/, message: '请输入有效的经度(-180 到 180)' }
                                ]}
                            >
                                <Input placeholder="请输入经度" type="number" step="0.000001" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="latitude"
                                label="纬度"
                                rules={[
                                    { pattern: /^-?((90)|(0?[0-8]?\d))(\.\d+)?$/, message: '请输入有效的纬度(-90 到 90)' }
                                ]}
                            >
                                <Input placeholder="请输入纬度" type="number" step="0.000001" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col span={24}>
                            <div style={{ marginBottom: '16px' }}>
                                <Button
                                    type="dashed"
                                    icon={<EnvironmentOutlined />}
                                    onClick={handleMapSelect}
                                    block
                                >
                                    在地图上选择位置
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    <Form.Item
                        name="status"
                        label="运行状态"
                        rules={[{ required: true, message: '请选择运行状态' }]}
                    >
                        <Select placeholder="请选择运行状态">
                            <Option value="online">在线</Option>
                            <Option value="offline">离线</Option>
                            <Option value="maintenance">维护中</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 地图选择模态框 */}
            <Modal
                title="在地图上选择位置"
                open={isMapModalOpen}
                onOk={handleMapConfirm}
                onCancel={() => setIsMapModalOpen(false)}
                width={800}
                okText="确认选择"
                cancelText="取消"
            >
                <div style={{ marginBottom: '10px', color: '#666' }}>
                    请在地图上点击选择场站位置
                    {selectedCoordinates && (
                        <span style={{ marginLeft: '10px', color: '#1890ff' }}>
                            已选择: 经度 {selectedCoordinates.lng.toFixed(6)}, 纬度 {selectedCoordinates.lat.toFixed(6)}
                        </span>
                    )}
                </div>
                <div
                    id="map-container"
                    style={{
                        width: '100%',
                        height: '400px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px'
                    }}
                />
            </Modal>
        </div>
    );
};

export default StationManagement; 